import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  MouseEvent
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

import { ThankYou } from './ThankYou';
import { Form } from './Form';

import { set } from '~/App/helpers/cookie';

import { CloseButton } from './components/CloseButton';
import { Footer } from './components/Footer';
import { TinyHeader } from '~/App/components/TinyHeader';
import { useQuery } from '~/App/shared/hooks/use-query';
import { featureIsEnabled } from '~/App/helpers/feature-is-enabled';
import { scrollAnimation } from '~/App/helpers/animations';

import { useScrollLock } from '~/App/contexts/ScrollLock';
import { useAppContext } from '~/App/contexts/App';
import { mq } from '~/lib/mq';
import { pushGTMTakeoverOverlayInteractions } from '~/App/helpers/gtm-helper';

type TakeOverProps = {
  showThankYou: boolean;
};

const TakeOver = styled.div<TakeOverProps>`
  position: fixed;
  background-color: #001489;
  background-image: url('https://res.cloudinary.com/cancerfonden/image/upload/v1761312581/article-images/Bildbakgrund_KS.jpg');
  background-size: cover;
  background-position-x: center;
  background-position-y: top;
  overflow: auto;
  width: 100vw;
  height: 100vh;
  z-index: 800;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ showThankYou }) =>
    showThankYou ? 'flex-start' : 'space-between'};
  overflow-y: auto;
`;

const Center = styled.div`
  margin: 1rem 0 0;
  text-align: center;

  ${mq('≥small')`
    margin: 1rem 0 1rem;
  `};
`;

const HideImbox = createGlobalStyle`
  #imbox-container {
    opacity: 0;
  }
`;

const includedUrls = [
  '/minska-risken',
  '/forskning',
  '/rad-och-stod',
  '/om-oss'
];

type Query = {
  source?: string;
  gala?: string | boolean;
};

type Props = {
  children: ReactNode;
};

export function Takeover({ children }: Props) {
  const location = useLocation();
  const query = useQuery<Query>();
  const scrollLock = useScrollLock();
  const cookies = useAppContext((x) => x.cookies);
  const navigate = useNavigate();

  const [isVisible, setVisible] = useState(() => {
    if (query.gala) {
      return true;
    }

    if (query.gala === false) {
      return false;
    }

    if (cookies._cf_takeover) {
      return false;
    }

    if (featureIsEnabled('FEATURE_CMS_STMC')) {
      for (const url of includedUrls) {
        if (location.pathname.startsWith(url) || location.pathname === '/') {
          return true;
        }
      }
    }

    return false;
  });

  const showThankYou = useMemo(() => query.gala === 'tack', [query.gala]);

  const handleClose = useCallback(
    (url?: string) => (e?: MouseEvent) => {
      e?.stopPropagation();
      if (url) {
        navigate(url);
      }

      set('_cf_takeover', 'true', 24);
      setVisible(false);
      scrollAnimation(0);

      if (showThankYou === false) {
        pushGTMTakeoverOverlayInteractions({
          action: 'close',
          entrance: 'takeover'
        });
      }
    },
    [navigate, showThankYou]
  );

  useEffect(() => {
    if (isVisible && !showThankYou) {
      pushGTMTakeoverOverlayInteractions({
        action: 'impression',
        entrance: 'takeover'
      });
    }
  }, [isVisible, showThankYou]);

  useEffect(() => (isVisible ? scrollLock.lock() : undefined), [
    isVisible,
    scrollLock
  ]);

  return useMemo(() => {
    if (!isVisible) {
      return children;
    }

    const closeButton = (
      <Center>
        <CloseButton
          onClose={handleClose()}
          text="Gå vidare till cancerfonden.se"
        />
      </Center>
    );

    const headerCloseButton = (
      <Center>
        <CloseButton onClose={handleClose('')} text="Till cancerfonden.se" />
      </Center>
    );

    return (
      <>
        <HideImbox />
        <TakeOver showThankYou={showThankYou}>
          <TinyHeader onClick={handleClose()} children={headerCloseButton} />
          {showThankYou ? (
            <ThankYou children={closeButton} onClose={handleClose} />
          ) : (
            <Form
              source={query.source}
              redirectPath="/?gala=tack"
              children={closeButton}
            />
          )}
          {showThankYou ? null : <Footer />}
        </TakeOver>
        {children}
      </>
    );
  }, [isVisible, showThankYou, query.source, children, handleClose]);
}
